import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getOrder, updateOrder, verifyPayment, clearOrder } from '../../actions/order';
import {
    Container,
    Row,
    Col,
    Table,
    Button,
    Card,
    Form,
    Badge,
    Alert,
    Spinner,
    Toast,
    ToastContainer,
    Modal
} from "react-bootstrap";


const Order = ({ getOrder, updateOrder, verifyPayment, clearOrder, order, loading }) => {
    const { id } = useParams();
    const [formData, setFormData] = useState({orderStatus: ""});
    const [checkButtonClick, setCheckButtonClick] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);

    const showAlert = (message, variant = 'success') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    useEffect(() => {
        document.title = 'Order Details';
        // Clear previous order data when ID changes
        clearOrder();
        // Fetch new order data
        getOrder(id);
        
        // Cleanup function to clear order when component unmounts
        return () => {
            clearOrder();
        };
    }, [id, getOrder, clearOrder]);

    useEffect(() => {
        // Update form data when order changes
        if (order) {
            setFormData({ orderStatus: order.orderStatus });
            setCheckButtonClick(false);
        }
    }, [order]);

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            await updateOrder(formData, order._id);
            showAlert('Order updated successfully!', 'success');
        } catch (error) {
            showAlert('Failed to update order. Please try again.', 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const checkPayment = async () => {
        try {
            setCheckButtonClick(true);
            await verifyPayment({_id: order._id});
            showAlert('Payment verification completed!', 'success');
            setShowVerifyModal(false);
        } catch (error) {
            showAlert('Payment verification failed. Please try again.', 'danger');
        }
    };

    const confirmVerifyPayment = () => {
        setShowVerifyModal(true);
    };

    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'created':
                return <Badge bg="info">Created</Badge>;
            case 'authorized':
                return <Badge bg="primary">Authorized</Badge>;
            case 'captured':
                return <Badge bg="success">Captured</Badge>;
            case 'refunded':
                return <Badge bg="warning">Refunded</Badge>;
            case 'failed':
                return <Badge bg="danger">Failed</Badge>;
            case 'canceled':
                return <Badge bg="secondary">Canceled</Badge>;
            default:
                return <Badge bg="info">{status || 'Unknown'}</Badge>;
        }
    };

    const getUserTypeBadge = (userType) => {
        switch(userType?.toLowerCase()) {
            case 'member':
                return <Badge bg="primary">Member</Badge>;
            case 'standard':
                return <Badge bg="secondary">Standard</Badge>;
            default:
                return <Badge bg="light" text="dark">{userType || 'Standard'}</Badge>;
        }
    };

    const renderProductPackageData = () => {
        if (!order || !order.productPackage || order.productPackage.length === 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan="6" className="text-center text-muted">
                            <i className="fas fa-box-open me-2"></i>
                            No product packages available
                        </td>
                    </tr>
                </tbody>
            );
        }

        const rows = [];
        
        order.productPackage.forEach((pkg, pkgIndex) => {
            // Add package header row
            rows.push(
                <tr key={`pkg-header-${pkgIndex}`} className="table-info">
                    <td colSpan="6" className="fw-bold">
                        <i className="fas fa-box me-2"></i>
                        Package: {pkg.name}
                        <Badge bg="primary" className="ms-2">{pkg.packageType}</Badge>
                        {pkg.validity && (
                            <Badge bg="secondary" className="ms-2">
                                Valid: {pkg.validity.days} days
                            </Badge>
                        )}
                    </td>
                </tr>
            );

            // Add bundle products from this package
            if (pkg.bundle && pkg.bundle.length > 0) {
                pkg.bundle.forEach((bundleItem, bundleIndex) => {
                    rows.push(
                        <tr key={`pkg-${pkgIndex}-bundle-${bundleIndex}`}>
                            <td>
                                <Badge bg="success">Bundle Item</Badge>
                            </td>
                            <td className="fw-bold">
                                {bundleItem.productType?.toUpperCase() || 'Product'}
                            </td>
                            <td className="text-center">{bundleItem.quantity}</td>
                            <td className="text-end">
                                ₹{bundleItem.priceInfo?.unitPrice?.toLocaleString() || 0}
                            </td>
                            <td className="text-end">
                                ₹{bundleItem.priceInfo?.basePrice?.toLocaleString() || 0}
                            </td>
                            <td className="text-end fw-bold text-success">
                                ₹{bundleItem.priceInfo?.price?.toLocaleString() || 0}
                            </td>
                        </tr>
                    );
                });
            }

            // Add package summary row
            rows.push(
                <tr key={`pkg-summary-${pkgIndex}`} className="table-warning">
                    <td colSpan="3" className="fw-bold">
                        Package Total (Qty: {pkg.quantity})
                    </td>
                    <td className="text-end fw-bold">GST: ₹{pkg.gstAmount?.toLocaleString() || 0}</td>
                    <td className="text-end fw-bold">Amount: ₹{pkg.amount?.toLocaleString() || 0}</td>
                    <td className="text-end fw-bold text-primary">
                        Total: ₹{pkg.amount?.toLocaleString() || 0}
                    </td>
                </tr>
            );
        });

        return <tbody>{rows}</tbody>;
    };

    const renderOrderForm = () => {
        if (!order) return null;

        return (
            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-edit me-2"></i>
                        Order Details
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-receipt me-2"></i>
                                        Order ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="orderId"
                                        value={order.orderId || ""}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-credit-card me-2"></i>
                                        Payment ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="paymentId"
                                        value={order.paymentId || "N/A"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Order Status
                                    </Form.Label>
                                    <div className="d-flex align-items-center">
                                        <Form.Select
                                            name="orderStatus"
                                            value={formData.orderStatus || ""}
                                            onChange={onChange}
                                            disabled={formData.orderStatus === "captured" || isSubmitting}
                                            className="me-2"
                                        >
                                            <option value="created">Created</option>
                                            <option value="authorized">Authorized</option>
                                            <option value="captured">Captured</option>
                                            <option value="refunded">Refunded</option>
                                            <option value="failed">Failed</option>
                                            <option value="canceled">Canceled</option>
                                        </Form.Select>
                                        {getStatusBadge(formData.orderStatus)}
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Payment Verification
                                    </Form.Label>
                                    <div>
                                        <Button
                                            variant="warning"
                                            onClick={confirmVerifyPayment}
                                            disabled={formData.orderStatus === "captured" || formData.orderStatus === "refunded" || checkButtonClick}
                                            className="w-100"
                                        >
                                            {checkButtonClick ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Verifying...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-search me-2"></i>
                                                    Verify Payment
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-percentage me-2"></i>
                                        GST Amount
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="gst"
                                        value={order.gstAmount ? `₹${order.gstAmount.toLocaleString()}` : "₹0"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-rupee-sign me-2"></i>
                                        Total Amount
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="amount"
                                        value={order.amount ? `₹${order.amount.toLocaleString()}` : "₹0"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-tag me-2"></i>
                                        MRP
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="mrp"
                                        value={order.mrp ? `₹${order.mrp.toLocaleString()}` : "₹0"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-clock me-2"></i>
                                        Date & Time
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="date"
                                        value={order.date ? new Date(order.date).toLocaleString() : "N/A"}
                                        disabled
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} className="text-center">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={isSubmitting}
                                    className="px-4"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save me-2"></i>
                                            Update Order
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    const renderUserData = () => {
        if(!order || !order.user) return (
            <Alert variant="warning" className="text-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                User information not available
            </Alert>
        );

        return (
            <Card className="shadow-sm h-100">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        User Details
                    </h5>
                </Card.Header>
                <Card.Body className="text-center">
                    <div className="mb-3">
                        <i className="fas fa-user-circle fa-3x text-info mb-3"></i>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-user me-2"></i>
                            Name:
                        </strong>
                        <div className="h6 mb-0">{order.user.name}</div>
                    </div>
                    <div className="mb-3">
                        <strong className="text-muted">
                            <i className="fas fa-phone me-2"></i>
                            Phone:
                        </strong>
                        <div className="h6 mb-0">{order.user.phone}</div>
                    </div>
                    {order.user.email && (
                        <div className="mb-3">
                            <strong className="text-muted">
                                <i className="fas fa-envelope me-2"></i>
                                Email:
                            </strong>
                            <div className="h6 mb-0">{order.user.email}</div>
                        </div>
                    )}
                    <div className="mb-4">
                        <strong className="text-muted">
                            <i className="fas fa-id-badge me-2"></i>
                            User Type:
                        </strong>
                        <div className="mt-1">
                            {getUserTypeBadge(order.userType)}
                        </div>
                    </div>
                    <Button
                        as={Link}
                        to={`/inventory/v1.2/users/${order.user._id}`}
                        variant="info"
                        size="sm"
                        className="px-3"
                    >
                        <i className="fas fa-eye me-2"></i>
                        View User Details
                    </Button>
                </Card.Body>
            </Card>
        );
    }


    const renderProductPackagesSection = () => {
        if (!order) return null;

        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">
                        <i className="fas fa-box me-2"></i>
                        Product Packages
                    </h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive striped hover className="mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Type</th>
                                <th>Product</th>
                                <th className="text-center">Quantity</th>
                                <th className="text-end">Unit Price (₹)</th>
                                <th className="text-end">Base Price (₹)</th>
                                <th className="text-end">Total Price (₹)</th>
                            </tr>
                        </thead>
                        {renderProductPackageData()}
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    const renderFreeCansSection = () => {
        if (!order || !order.freeCans || order.freeCans.quantity === 0) return null;

        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-gift me-2"></i>
                        Free Cans
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <div className="d-flex align-items-center">
                                <i className="fas fa-can-food fa-2x text-success me-3"></i>
                                <div>
                                    <h6 className="mb-1">
                                        Brand: <span className="fw-bold">{order.freeCans.product?.brand}</span>
                                    </h6>
                                    <p className="text-muted mb-0">Complimentary cans included with your order</p>
                                </div>
                            </div>
                        </Col>
                        <Col md={4} className="text-end">
                            <Badge bg="success" className="fs-6 px-3 py-2">
                                <i className="fas fa-gift me-2"></i>
                                {order.freeCans.quantity} Free Cans
                            </Badge>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderReferralSection = () => {
        if (!order || !order.referral) return null;

        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-users me-2"></i>
                        Referral Information
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong className="text-muted">
                                    <i className="fas fa-user me-2"></i>
                                    Referred By:
                                </strong>
                                <div className="h6 mb-0">{order.referral.user?.name}</div>
                            </div>
                            <div className="mb-3">
                                <strong className="text-muted">
                                    <i className="fas fa-tag me-2"></i>
                                    Referral ID:
                                </strong>
                                <div className="h6 mb-0">
                                    <Badge bg="info" className="fs-6">{order.referral.referralId}</Badge>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <strong className="text-muted">
                                    <i className="fas fa-gift me-2"></i>
                                    Referral Cans:
                                </strong>
                                <div className="h6 mb-0">
                                    <Badge bg="success" className="fs-6">
                                        {order.referral.cans} Cans
                                    </Badge>
                                </div>
                            </div>
                            {order.referredCans && (
                                <div className="mb-3">
                                    <strong className="text-muted">
                                        <i className="fas fa-share me-2"></i>
                                        Referred Cans:
                                    </strong>
                                    <div className="h6 mb-0">
                                        <Badge bg="warning" className="fs-6">
                                            {order.referredCans.quantity} {order.referredCans.product?.brand} Cans
                                        </Badge>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderOrderSummarySection = () => {
        if (!order) return null;

        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-calculator me-2"></i>
                        Order Summary
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                <span><i className="fas fa-tag me-2"></i>MRP:</span>
                                <span className="fw-bold">₹{order.mrp?.toLocaleString()}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                <span><i className="fas fa-percentage me-2"></i>GST:</span>
                                <span className="fw-bold">₹{order.gstAmount?.toLocaleString()}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                <span><i className="fas fa-piggy-bank me-2"></i>Savings:</span>
                                <span className="fw-bold text-success">₹{order.savings?.toLocaleString()}</span>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                <span><i className="fas fa-percent me-2"></i>Discount:</span>
                                <span className="fw-bold text-success">₹{order.discount?.toLocaleString()}</span>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center p-3 bg-primary text-white rounded">
                                <span className="fs-5">
                                    <i className="fas fa-rupee-sign me-2"></i>
                                    Final Amount:
                                </span>
                                <span className="fs-4 fw-bold">₹{order.amount?.toLocaleString()}</span>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderVerificationModal = () => (
        <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
            <Modal.Header closeButton className="bg-warning text-dark">
                <Modal.Title>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Confirm Payment Verification
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="text-center">
                    <i className="fas fa-credit-card fa-3x text-warning mb-3"></i>
                    <h5>Are you sure you want to verify this payment?</h5>
                    <p className="text-muted">
                        This action will check the payment status with the payment gateway.
                    </p>
                    <div className="bg-light p-3 rounded mt-3">
                        <strong>Order ID:</strong> {order?.orderId}<br/>
                        <strong>Payment ID:</strong> {order?.paymentId || 'N/A'}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>
                    <i className="fas fa-times me-2"></i>
                    Cancel
                </Button>
                <Button variant="warning" onClick={checkPayment} disabled={checkButtonClick}>
                    {checkButtonClick ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Verifying...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-check me-2"></i>
                            Verify Payment
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <div className="mt-3">
                        <h5>Loading order details...</h5>
                        <p className="text-muted">Please wait while we fetch the information.</p>
                    </div>
                </div>
            </Container>
        );
    }

    if (!order) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <i className="fas fa-exclamation-triangle fa-2x mb-3"></i>
                    <h4>Order Not Found</h4>
                    <p>The requested order could not be found or does not exist.</p>                   
                    <Link className="btn btn-outline-primary d-flex align-items-center mb-2" 
                        to="/inventory/v1.2/orders">
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Orders
                    </Link>
                </Alert>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container fluid className="py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Link 
                                    className="btn btn-outline-primary d-flex align-items-center mb-2" 
                                    to="/inventory/v1.2/orders"
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Orders
                                </Link>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <i className="fas fa-receipt me-3 text-primary" style={{fontSize: '32px'}}></i>
                                    Order Management
                                </h1>
                                <p className="text-muted mb-0">
                                    Manage and update order details
                                    {order && (
                                        <>
                                            {' - '}
                                            <Badge bg="secondary" className="me-2">
                                                ID: {order.orderId}
                                            </Badge>
                                            {getStatusBadge(order.orderStatus)}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <Row className="g-4">
                    <Col lg={8}>
                        {renderOrderForm()}
                        {renderProductPackagesSection()}
                        {renderFreeCansSection()}
                        {renderReferralSection()}
                        {renderOrderSummarySection()}
                    </Col>
                    <Col lg={4}>
                        <div className="mb-4">
                            {renderUserData()}
                        </div>
                    </Col>
                </Row>
            </Container>

            {renderVerificationModal()}

            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={3000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </Fragment>
    )

}

Order.propTypes = {
    getOrder: PropTypes.func.isRequired,
    updateOrder: PropTypes.func.isRequired,
    verifyPayment: PropTypes.func.isRequired,
    clearOrder: PropTypes.func.isRequired,
    order: PropTypes.object,
    loading: PropTypes.bool
} 
const mapStateToProps = state => ({
    order: state.order.order,
    loading: state.order.loading
})

export default connect( mapStateToProps, { getOrder, updateOrder, verifyPayment, clearOrder } )( Order );